import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Mic, Sparkles, Loader2, Volume2, Save, Trash2, Folder, RefreshCw, Send,
  Newspaper, Music2, Film, MapPin, Trophy, Cloud, TrafficCone, Clock, AlertTriangle,
  Globe2, Headphones, Radio, MessageCircle, Twitter, Instagram, Facebook, Youtube,
  Download, Calendar, Wand2, Library, PlayCircle, FileText,
  Sliders, ChevronDown, ChevronUp, Inbox, ExternalLink,
  Disc3, Settings, Zap, User,
} from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import SendToAvatarButton from "@/components/SendToAvatarButton";
import VisualStyleSelector from "@/components/news/VisualStyleSelector";
import AiWriteScriptModal, { AiWriteButton } from "@/components/AiWriteScriptModal";
import ContentLanguageSelect from "@/components/ContentLanguageSelect";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { SendToPrompterButton } from "@/components/SendToPrompterButton";
import { appConfirm } from "@/components/AppDialogHost";

const ICON_FOR_TYPE = {
  headline_news: Newspaper, music_news: Music2, entertainment_news: Film,
  local_news: MapPin, sports_news: Trophy, weather: Cloud,
  traffic: TrafficCone, top_of_hour: Clock, breaking_news: AlertTriangle,
  archived: Folder,
};

const PLATFORM_META = {
  website:    { color: "#0078D4", label: "Website",    Icon: Globe2 },
  podcast:    { color: "#0093FF", label: "Podcast",    Icon: Headphones },
  mobile_app: { color: "#8B5CF6", label: "Mobile app", Icon: Radio },
  wordpress:  { color: "#21759B", label: "WordPress",  Icon: ExternalLink },
  facebook:   { color: "#1877F2", label: "Facebook",   Icon: Facebook },
  instagram:  { color: "#E1306C", label: "Instagram",  Icon: Instagram },
  x:          { color: "#94A3B8", label: "X",          Icon: Twitter },
  tiktok:     { color: "#25F4EE", label: "TikTok",     Icon: Music2 },
  youtube:    { color: "#FF0000", label: "YouTube",    Icon: Youtube },
  whatsapp:   { color: "#25D366", label: "WhatsApp",   Icon: MessageCircle },
};

export default function NewsStudio() {
  const [tpl, setTpl] = useState(null);
  const [beds, setBeds] = useState(null);            // /news-studio/beds payload
  const [tab, setTab] = useState("compose"); // compose | library
  const [bulletins, setBulletins] = useState([]);
  const [libBusy, setLibBusy] = useState(false);
  const [folderFilter, setFolderFilter] = useState("");

  // compose state
  const [activeId, setActiveId] = useState(null);
  const [bulletin, setBulletin] = useState(null);
  const [busy, setBusy] = useState({});
  const [previewTargets, setPreviewTargets] = useState(["website"]);

  // Advanced mix settings (sent on render)
  const [mix, setMix] = useState({
    bed_subtype: "talk_warm",
    voice_volume_db: -3,
    bed_volume_db: -16,
    fade_in_ms: 600,
    fade_out_ms: 800,
    normalize: true,
  });

  // Visual Style Selector — Update #81
  // Persisted to localStorage so when the user broadcasts this bulletin from
  // News Center, the same style is used by default.
  const VISUAL_STYLE_KEY = "bf_news_visual_style_default";
  const [visualPick, setVisualPick] = useState(() => {
    try {
      const raw = localStorage.getItem(VISUAL_STYLE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && parsed.visual_style) return parsed;
    } catch { /* corrupt cache, ignore */ }
    return { visual_style: "avatar", avatar_id: "amy", avatar_source_url: undefined };
  });
  useEffect(() => {
    try { localStorage.setItem(VISUAL_STYLE_KEY, JSON.stringify(visualPick)); }
    catch { /* storage blocked */ }
  }, [visualPick]);

  // Import-from-News-Center modal
  const [importOpen, setImportOpen] = useState(false);

  // Now-playing preview (refreshes every 30s) — powers the live header badge
  // and the "From now playing" one-click bulletin shortcut.
  const [nowPlaying, setNowPlaying] = useState(null);
  const fetchNowPlaying = async () => {
    try {
      const { data } = await api.get("/news-studio/now-playing-preview");
      setNowPlaying(data);
    } catch { /* silently ignore — feature is optional */ }
  };
  useEffect(() => {
    fetchNowPlaying();
    const t = setInterval(fetchNowPlaying, 30_000);
    return () => clearInterval(t);
  }, []);

  // ── Deep-link handoff from News Ingestion (Update #170) ──────
  // News Ingestion's "Send to News Studio" flow creates a bulletin,
  // then navigates here with `?bulletin={id}`. We open it on the
  // Compose tab so the DJ can review, tweak, and render.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const bid = searchParams.get("bulletin");
    if (bid) {
      (async () => {
        try {
          const { data } = await api.get(`/news-studio/bulletins/${bid}`);
          setBulletin(data); setActiveId(bid); setTab("compose");
          toast.success("Opened bulletin from News Ingestion");
          // Strip the query param so a reload doesn't re-fetch
          const next = new URLSearchParams(searchParams);
          next.delete("bulletin");
          setSearchParams(next, { replace: true });
        } catch {
          toast.error("Could not open the linked bulletin");
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-draft settings modal
  const [autoOpen, setAutoOpen] = useState(false);
  const [autoSettings, setAutoSettings] = useState(null);
  const loadAutoSettings = async () => {
    try {
      const { data } = await api.get("/news-studio/auto-settings");
      setAutoSettings(data);
    } catch { /* optional */ }
  };
  useEffect(() => { loadAutoSettings(); }, []);

  const fromNowPlaying = async (duration_key = "radio_30") => {
    setBusy((b) => ({ ...b, nowPlaying: true }));
    try {
      const { data } = await api.post("/news-studio/from-now-playing", { duration_key });
      setBulletin(data.bulletin);
      setActiveId(data.bulletin.bulletin_id);
      setTab("compose");
      // Pre-select the resolved bed in Advanced Mix so a one-click render uses it
      if (data.resolved?.bed_key) setMix((m) => ({ ...m, bed_subtype: data.resolved.bed_key }));
      loadLibrary(folderFilter);
      toast.success(`Drafted from "${data.now_playing.artist} — ${data.now_playing.title}"`);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) toast.error("No track is currently spinning — start the Now Playing poller first.", { duration: 6000 });
      else if (status === 402) toast.error("AI budget exhausted — top up Universal LLM Key.", { duration: 8000 });
      else toast.error(e?.response?.data?.detail || "Couldn't draft from now-playing");
    } finally { setBusy((b) => ({ ...b, nowPlaying: false })); }
  };

  useEffect(() => {
    api.get("/news-studio/templates").then((r) => setTpl(r.data)).catch(() => toast.error("Failed to load templates"));
    api.get("/news-studio/beds").then((r) => setBeds(r.data)).catch(() => {/* beds optional */});
    loadLibrary();
  }, []);

  const loadLibrary = async (folder = "") => {
    setLibBusy(true);
    try {
      const { data } = await api.get(`/news-studio/bulletins${folder ? `?folder=${folder}` : ""}`);
      setBulletins(data || []);
    } catch { toast.error("Failed to load library"); }
    finally { setLibBusy(false); }
  };

  const newBulletin = async (bulletin_type = "headline_news") => {
    setBusy((b) => ({ ...b, create: true }));
    try {
      const { data } = await api.post("/news-studio/bulletins", {
        bulletin_type, duration_key: "radio_30", voice_id: "female_news",
        branding: {},
      });
      setBulletin(data); setActiveId(data.bulletin_id); setTab("compose");
      loadLibrary(folderFilter);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create bulletin"); }
    finally { setBusy((b) => ({ ...b, create: false })); }
  };

  // Create a bulletin pre-populated from a News Center story.
  // `story` is the row from /api/news/stories. We let the backend hydrate the
  // raw_script from the chosen duration's generated copy via `source_story_id`.
  const importFromStory = async (story, duration_key = "radio_30", bulletin_type = "headline_news") => {
    setBusy((b) => ({ ...b, importStory: true }));
    try {
      const { data } = await api.post("/news-studio/bulletins", {
        title: story.headline || story.title || "Imported bulletin",
        bulletin_type,
        duration_key,
        voice_id: "female_news",
        source_story_id: story.story_id,
        branding: {},
      });
      setBulletin(data); setActiveId(data.bulletin_id); setTab("compose");
      setImportOpen(false);
      loadLibrary(folderFilter);
      toast.success(`Imported "${(story.headline || story.title || "").slice(0, 60)}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy((b) => ({ ...b, importStory: false })); }
  };

  const open = async (bid) => {
    try {
      const { data } = await api.get(`/news-studio/bulletins/${bid}`);
      setBulletin(data); setActiveId(bid); setTab("compose");
    } catch { toast.error("Couldn't open bulletin"); }
  };

  const patch = async (updates) => {
    if (!activeId) return;
    try {
      const { data } = await api.patch(`/news-studio/bulletins/${activeId}`, updates);
      setBulletin(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const runFormat = async () => {
    if (!bulletin?.raw_script?.trim()) { toast.error("Type or paste a script first"); return; }
    setBusy((b) => ({ ...b, format: true }));
    try {
      const { data } = await api.post(`/news-studio/bulletins/${activeId}/auto-format`);
      setBulletin((b) => ({ ...b, formatted_script: data.formatted_script }));
      toast.success(`Script formatted · ${data.word_count} words`);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) toast.error("AI budget exhausted — top up Universal LLM Key.", { duration: 8000 });
      else toast.error(e?.response?.data?.detail || "Format failed");
    } finally { setBusy((b) => ({ ...b, format: false })); }
  };

  const runRender = async () => {
    if (!bulletin?.formatted_script && !bulletin?.raw_script) { toast.error("Script missing"); return; }
    setBusy((b) => ({ ...b, render: true }));
    try {
      const { data } = await api.post(`/news-studio/bulletins/${activeId}/render`, {
        voice_id: bulletin.voice_id,
        bed_subtype: mix.bed_subtype,
        voice_volume_db: Number(mix.voice_volume_db),
        bed_volume_db: Number(mix.bed_volume_db),
        fade_in_ms: Number(mix.fade_in_ms),
        fade_out_ms: Number(mix.fade_out_ms),
        normalize: !!mix.normalize,
      });
      toast.success(`Bulletin rendered · ${data.duration_seconds}s`);
      // refresh
      const r = await api.get(`/news-studio/bulletins/${activeId}`);
      setBulletin(r.data); loadLibrary(folderFilter);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) toast.error("AI voice budget exhausted — top up Universal LLM Key.", { duration: 8000 });
      else toast.error(e?.response?.data?.detail || "Render failed");
    } finally { setBusy((b) => ({ ...b, render: false })); }
  };

  const publishNow = async () => {
    if (!bulletin?.audio_asset_id) { toast.error("Render the bulletin first"); return; }
    setBusy((b) => ({ ...b, publish: true }));
    try {
      const { data } = await api.post(`/news-studio/bulletins/${activeId}/publish`, {
        targets: previewTargets,
        caption: bulletin.title,
      });
      toast.success(`Published to ${data.posted_to.length} target${data.posted_to.length === 1 ? "" : "s"}`);
      const r = await api.get(`/news-studio/bulletins/${activeId}`);
      setBulletin(r.data); loadLibrary(folderFilter);
    } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
    finally { setBusy((b) => ({ ...b, publish: false })); }
  };

  const scheduleSlot = async (slot) => {
    if (!bulletin?.audio_asset_id) { toast.error("Render first, then schedule"); return; }
    try {
      const { data } = await api.post(`/news-studio/bulletins/${activeId}/schedule`, { slot });
      toast.success(`Scheduled for ${slot.replace("_", " ")}`);
      const r = await api.get(`/news-studio/bulletins/${activeId}`);
      setBulletin(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Schedule failed"); }
  };

  const removeBulletin = async (bid) => {
    if (!await appConfirm("Delete this bulletin?")) return;
    try {
      await api.delete(`/news-studio/bulletins/${bid}`);
      if (activeId === bid) { setBulletin(null); setActiveId(null); }
      loadLibrary(folderFilter);
    } catch { toast.error("Delete failed"); }
  };

  if (!tpl) {
    return (
      <div className="p-10 text-center text-ink-muted text-sm">
        <Loader2 className="h-5 w-5 mx-auto animate-spin text-brand-amber mb-2" />
        Loading studio…
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 animate-fade-up max-w-7xl mx-auto" data-testid="news-studio-page">
      <PageHeader
        eyebrow="// AI NEWS ANCHOR STUDIO"
        title="Ready-to-air bulletins in seconds."
        description="Script → AI format → AI voice → ducked bed → exportable MP3. Built for headlines, music news, sports, weather, traffic and breaking alerts."
        action={
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {nowPlaying?.now_playing && (
              <button
                onClick={() => fromNowPlaying("radio_30")}
                disabled={busy.nowPlaying}
                data-testid="ns-from-nowplaying-btn"
                title={`Mood: ${nowPlaying.resolved?.mood || "—"} · Voice: ${nowPlaying.resolved?.voice_label || "—"} · Bed: ${nowPlaying.resolved?.bed_label || "—"}`}
                className="group inline-flex items-center gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200 hover:text-emerald-100 hover:border-emerald-400 transition disabled:opacity-50 max-w-[280px]"
              >
                {busy.nowPlaying
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
                  : <Disc3 className="h-3.5 w-3.5 shrink-0 animate-[spin_8s_linear_infinite]" />}
                <span className="flex flex-col items-start min-w-0 leading-tight text-left">
                  <span className="font-mono uppercase text-[9px] tracking-wider text-emerald-300/80">On air now</span>
                  <span className="truncate w-full text-[11px] font-semibold">
                    {nowPlaying.now_playing.artist} — {nowPlaying.now_playing.title}
                  </span>
                </span>
              </button>
            )}
            <button
              onClick={() => setImportOpen(true)}
              data-testid="ns-import-btn"
              className="inline-flex items-center gap-2 rounded-md border border-cyan-500/40 bg-cyan-500/10 px-4 py-2 text-sm text-cyan-200 hover:text-cyan-100 hover:border-cyan-400 transition"
            >
              <Inbox className="h-4 w-4" /> Import from News Center
            </button>
            <button
              onClick={() => setAutoOpen(true)}
              data-testid="ns-auto-settings-btn"
              title="Auto-draft from now-playing"
              className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition ${
                autoSettings?.enabled
                  ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-200 hover:text-emerald-100"
                  : "border-line bg-bg-elevated text-ink-secondary hover:text-ink-primary"
              }`}
            >
              {autoSettings?.enabled
                ? <Zap className="h-4 w-4 fill-emerald-300/40" />
                : <Settings className="h-4 w-4" />}
              <span className="hidden sm:inline">
                Auto-draft{autoSettings?.enabled ? " · ON" : ""}
              </span>
            </button>
            <button
              onClick={() => setTab(tab === "library" ? "compose" : "library")}
              data-testid="ns-tab-toggle"
              className="inline-flex items-center gap-2 rounded-md border border-line bg-bg-elevated px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary transition"
            >
              <Library className="h-4 w-4" /> {tab === "library" ? "Compose" : "Library"}
            </button>
            <Link
              to="/dashboard/news-studio/avatars"
              data-testid="ns-my-avatars-link"
              className="inline-flex items-center gap-2 rounded-md border border-line bg-bg-elevated px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary transition"
              title="Manage custom on-air avatars"
            >
              <User className="h-4 w-4" /> My Avatars
            </Link>
            <button
              onClick={() => newBulletin()}
              disabled={busy.create}
              data-testid="ns-new-btn"
              className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-5 py-2.5 font-semibold text-black amber-glow disabled:opacity-50"
            >
              {busy.create ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}
              New bulletin
            </button>
          </div>
        }
      />

      {tab === "library" && (
        <LibraryView
          tpl={tpl}
          bulletins={bulletins}
          loading={libBusy}
          folderFilter={folderFilter}
          setFolderFilter={(f) => { setFolderFilter(f); loadLibrary(f); }}
          onOpen={open}
          onDelete={removeBulletin}
          onNewType={newBulletin}
        />
      )}

      {tab === "compose" && !bulletin && (
        <EmptyState tpl={tpl} onCreate={newBulletin} />
      )}

      {tab === "compose" && bulletin && (
        <ComposeView
          tpl={tpl}
          beds={beds}
          bulletin={bulletin}
          setBulletin={setBulletin}
          patch={patch}
          busy={busy}
          mix={mix}
          setMix={setMix}
          onFormat={runFormat}
          onRender={runRender}
          onPublish={publishNow}
          onSchedule={scheduleSlot}
          previewTargets={previewTargets}
          setPreviewTargets={setPreviewTargets}
          visualPick={visualPick}
          setVisualPick={setVisualPick}
        />
      )}

      <ImportFromNewsCenterModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onPick={importFromStory}
        busy={!!busy.importStory}
        tpl={tpl}
      />

      <AutoDraftSettingsModal
        open={autoOpen}
        onClose={() => setAutoOpen(false)}
        tpl={tpl}
        settings={autoSettings}
        onSaved={(s) => setAutoSettings((prev) => ({ ...(prev || {}), ...s }))}
      />
    </div>
  );
}

function EmptyState({ tpl, onCreate }) {
  return (
    <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="ns-empty-grid">
      {tpl.bulletin_types.map((bt) => {
        const Icon = ICON_FOR_TYPE[bt.id] || Newspaper;
        return (
          <button
            key={bt.id}
            onClick={() => onCreate(bt.id)}
            data-testid={`ns-empty-${bt.id}`}
            className="group rounded-2xl border border-line bg-bg-surface p-5 text-left hover:border-brand-amber/40 transition"
          >
            <div className="h-10 w-10 rounded-lg bg-brand-amber/10 border border-brand-amber/40 flex items-center justify-center mb-3">
              <Icon className="h-5 w-5 text-brand-amber" />
            </div>
            <div className="font-display font-bold text-sm">{bt.label}</div>
            <div className="text-[10px] text-ink-muted mt-0.5">Default {bt.default_dur.replace("radio_", "")}s</div>
          </button>
        );
      })}
    </div>
  );
}

function ComposeView({ tpl, beds, bulletin, setBulletin, patch, busy, mix, setMix, onFormat, onRender, onPublish, onSchedule, previewTargets, setPreviewTargets, visualPick, setVisualPick }) {
  const Icon = ICON_FOR_TYPE[bulletin.bulletin_type] || Newspaper;
  const wordCount = (bulletin.formatted_script || bulletin.raw_script || "").trim().split(/\s+/).filter(Boolean).length;
  const [aiOpen, setAiOpen] = useState(false);

  // Map News Anchor bulletin types → best-fit script_type for the AI Writer.
  const scriptTypeForBulletin =
    ({ headline_news: "news", music_news: "news", sports: "news",
       weather:       "weather", traffic:    "traffic", breaking:  "news",
     })[bulletin.bulletin_type] || "news";
  // Duration key → target seconds for the modal's length picker.
  const targetSeconds =
    ({ radio_15: 15, radio_30: 30, radio_60: 60, radio_90: 90, radio_120: 120 }
       [bulletin.duration_key]) || 30;

  return (
    <div className="mt-6 grid lg:grid-cols-3 gap-4" data-testid="ns-compose">
      {/* LEFT: meta + scripts */}
      <div className="lg:col-span-2 space-y-4">
        {/* Title row */}
        <div className="flex items-center gap-3 rounded-xl border border-line bg-bg-surface p-4">
          <div className="h-9 w-9 rounded-lg bg-brand-amber/10 border border-brand-amber/40 flex items-center justify-center">
            <Icon className="h-4 w-4 text-brand-amber" />
          </div>
          <input
            value={bulletin.title}
            onChange={(e) => setBulletin((b) => ({ ...b, title: e.target.value }))}
            onBlur={() => patch({ title: bulletin.title })}
            className="flex-1 bg-transparent text-lg font-display font-bold text-ink-primary outline-none"
            placeholder="Bulletin title"
            data-testid="ns-title"
          />
          <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">{bulletin.status}</span>
        </div>

        {/* Type + duration selectors */}
        <div className="grid sm:grid-cols-3 gap-3 rounded-xl border border-line bg-bg-surface p-4">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Bulletin type</Label>
            <Select value={bulletin.bulletin_type} onValueChange={(v) => patch({ bulletin_type: v, folder: v })}>
              <SelectTrigger className="bg-bg border-line mt-1" data-testid="ns-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {tpl.bulletin_types.map((bt) => (
                  <SelectItem key={bt.id} value={bt.id} className="text-xs">{bt.icon} {bt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Duration</Label>
            <Select value={bulletin.duration_key} onValueChange={(v) => patch({ duration_key: v })}>
              <SelectTrigger className="bg-bg border-line mt-1" data-testid="ns-duration"><SelectValue /></SelectTrigger>
              <SelectContent>
                {tpl.durations.map((d) => (
                  <SelectItem key={d.id} value={d.id} className="text-xs">{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Word count</Label>
            <div className="mt-2 font-display text-2xl font-black text-brand-amber" data-testid="ns-wordcount">{wordCount}</div>
          </div>
        </div>

        {/* Raw script */}
        <div className="rounded-xl border border-line bg-bg-surface p-4">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-[10px] uppercase tracking-wider text-brand-amber">// STEP 1 · RAW SOURCE TEXT</Label>
            <div className="flex items-center gap-2">
              <ContentLanguageSelect
                value={bulletin.language || "en"}
                onChange={(code) => {
                  setBulletin((b) => ({ ...b, language: code }));
                  patch({ language: code });
                }}
                testid="ns-language"
                compact
                label={null}
              />
              <SendToPrompterButton
                title={bulletin.title || "News bulletin"}
                text={bulletin.formatted_script || bulletin.raw_script}
                source="news_studio"
                testid="ns-send-prompter"
              />
              <AiWriteButton onClick={() => setAiOpen(true)} testid="ns-ai-write" label="Write for me" />
              <button
                onClick={onFormat}
                disabled={busy.format || !bulletin.raw_script?.trim()}
                data-testid="ns-format-btn"
                className="inline-flex items-center gap-1.5 rounded-md bg-violet-500 text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
              >
                {busy.format ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
                AI Format
              </button>
            </div>
          </div>
          <Textarea
            value={bulletin.raw_script}
            onChange={(e) => setBulletin((b) => ({ ...b, raw_script: e.target.value }))}
            onBlur={() => patch({ raw_script: bulletin.raw_script })}
            rows={5}
            placeholder="Paste a news article, headlines, traffic notes, weather data, or your own writeup…"
            className="bg-bg border-line text-sm"
            data-testid="ns-raw"
          />
        </div>

        {/* Formatted script */}
        <div className="rounded-xl border border-line bg-bg-surface p-4">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-[10px] uppercase tracking-wider text-brand-amber">// STEP 2 · BROADCAST SCRIPT</Label>
            <div className="flex items-center gap-2">
              <SendToAvatarButton
                script={bulletin.formatted_script || bulletin.raw_script}
                title={bulletin.title || "News bulletin"}
                variant="compact"
                testid="news-send-to-avatar"
              />
              <span className="text-[10px] text-ink-muted">Editable — the AI will use what&apos;s in this box</span>
            </div>
          </div>
          <Textarea
            value={bulletin.formatted_script}
            onChange={(e) => setBulletin((b) => ({ ...b, formatted_script: e.target.value }))}
            onBlur={() => patch({ formatted_script: bulletin.formatted_script })}
            rows={6}
            placeholder='Click "AI Format" above to auto-write a broadcast-ready script — or type your own here.'
            className="bg-bg border-line text-sm font-mono"
            data-testid="ns-formatted"
          />
        </div>

        {/* Step 3 — audio player + render */}
        <div className="rounded-xl border border-line bg-bg-surface p-4">
          <div className="flex items-center justify-between mb-3">
            <Label className="text-[10px] uppercase tracking-wider text-brand-amber">// STEP 3 · RENDER &amp; PLAY</Label>
            <button
              onClick={onRender}
              disabled={busy.render || (!bulletin.formatted_script && !bulletin.raw_script)}
              data-testid="ns-render-btn"
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-4 py-2 text-xs font-bold text-black disabled:opacity-50 amber-glow"
            >
              {busy.render ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mic className="h-3.5 w-3.5" />}
              {bulletin.audio_asset_id ? "Re-render" : "Render bulletin"}
            </button>
          </div>
          {bulletin.audio_asset_id ? (
            <div className="space-y-2">
              <audio
                controls
                src={`${API_BASE}/news-studio/bulletins/${bulletin.bulletin_id}/audio`}
                className="w-full"
                data-testid="ns-audio"
              />
              <div className="flex items-center justify-between text-[11px] text-ink-muted gap-2 flex-wrap">
                <span>{Math.round((bulletin.duration_ms || 0) / 1000)}s · ducked bed · normalized</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={async () => {
                      try {
                        const { data } = await api.post(`/news-studio/bulletins/${bulletin.bulletin_id}/push-to-myriad`);
                        toast.success(`Pushed to Myriad · cart ${data.cart_id || "—"}`);
                        // Refresh bulletin to surface myriad_push log
                        const r = await api.get(`/news-studio/bulletins/${bulletin.bulletin_id}`);
                        setBulletin(r.data);
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Push to Myriad failed");
                      }
                    }}
                    data-testid="ns-push-myriad"
                    title="Push this rendered bulletin straight into your connected Broadcast Radio (Myriad) cart wall"
                    className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[10px] text-emerald-200 hover:text-emerald-100 hover:border-emerald-400 transition"
                  ><Send className="h-3 w-3" /> Push to Myriad</button>
                  <a
                    href={`${API_BASE}/news-studio/bulletins/${bulletin.bulletin_id}/audio`}
                    download={`${bulletin.title || "bulletin"}.mp3`}
                    data-testid="ns-download"
                    className="inline-flex items-center gap-1.5 rounded-md bg-cyan-500/15 border border-cyan-400/50 px-3 py-1.5 text-[11px] font-bold text-cyan-300 hover:bg-cyan-500/25 transition"
                  ><Download className="h-3.5 w-3.5" /> Download MP3</a>
                </div>
              </div>
              {bulletin.myriad_push?.cart_id && (
                <div className="text-[10px] text-emerald-300/80 font-mono" data-testid="ns-myriad-status">
                  ★ Pushed to Myriad cart #{bulletin.myriad_push.cart_id} ·{" "}
                  {new Date(bulletin.myriad_push.pushed_at).toLocaleString()}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6 border border-dashed border-line rounded-md text-[11px] text-ink-muted">
              <Volume2 className="h-4 w-4 text-brand-amber mx-auto mb-1" />
              Render to generate the audio bulletin.
            </div>
          )}

          {/* Advanced mix controls (collapsible) */}
          <AdvancedMixPanel mix={mix} setMix={setMix} beds={beds} />
        </div>
      </div>

      {/* RIGHT: voice + branding + publish + schedule */}
      <div className="space-y-4">
        {/* Voice picker */}
        <section className="rounded-xl border border-line bg-bg-surface p-4">
          <Label className="text-[10px] uppercase tracking-wider text-brand-amber">// AI VOICE</Label>
          <div className="mt-2 grid gap-1.5 max-h-72 overflow-y-auto pr-1">
            {tpl.voices.map((v) => {
              const sel = bulletin.voice_id === v.id;
              return (
                <button
                  key={v.id}
                  onClick={() => patch({ voice_id: v.id })}
                  data-testid={`ns-voice-${v.id}`}
                  className={`text-left rounded-lg border p-2.5 transition ${sel ? "border-brand-amber bg-brand-amber/10" : "border-line bg-bg-elevated hover:border-brand-amber/40"}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-semibold text-ink-primary">{v.label}</div>
                    {sel && <span className="text-[9px] font-mono text-brand-amber">SELECTED</span>}
                  </div>
                  <div className="text-[10px] text-ink-muted mt-0.5">{v.vibe}</div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Branding */}
        <section className="rounded-xl border border-line bg-bg-surface p-4 space-y-2">
          <Label className="text-[10px] uppercase tracking-wider text-brand-amber">// STATION BRANDING</Label>
          {["station_name", "slogan", "intro_liner", "outro_liner"].map((k) => (
            <Input
              key={k}
              value={(bulletin.branding || {})[k] || ""}
              onChange={(e) => setBulletin((b) => ({ ...b, branding: { ...(b.branding || {}), [k]: e.target.value } }))}
              onBlur={() => patch({ branding: bulletin.branding })}
              placeholder={k.replace("_", " ")}
              className="bg-bg border-line text-xs"
              data-testid={`ns-brand-${k}`}
            />
          ))}
        </section>

        {/* Schedule */}
        <section className="rounded-xl border border-line bg-bg-surface p-4 space-y-2">
          <Label className="text-[10px] uppercase tracking-wider text-brand-amber">// SCHEDULE</Label>
          <div className="grid grid-cols-2 gap-1.5">
            {["top_of_hour", "morning", "midday", "afternoon", "evening", "weekend"].map((s) => (
              <button
                key={s}
                onClick={() => onSchedule(s)}
                disabled={!bulletin.audio_asset_id}
                data-testid={`ns-schedule-${s}`}
                className="rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-[10px] font-mono uppercase tracking-wider hover:border-brand-amber/40 disabled:opacity-40"
              ><Calendar className="h-2.5 w-2.5 inline mr-1" />{s.replace("_", " ")}</button>
            ))}
          </div>
          {bulletin.schedule && (
            <div className="text-[10px] text-emerald-300 mt-1">
              ✓ Scheduled · {new Date(bulletin.schedule.scheduled_for).toLocaleString()}
            </div>
          )}
        </section>

        {/* Publish */}
        <section className="rounded-xl border border-line bg-bg-surface p-4 space-y-2">
          <Label className="text-[10px] uppercase tracking-wider text-brand-amber">// PUBLISH</Label>
          <div className="grid grid-cols-3 gap-1.5">
            {tpl.publish_targets.map((p) => {
              const meta = PLATFORM_META[p] || { color: "#94A3B8", label: p, Icon: Send };
              const on = previewTargets.includes(p);
              const Icon = meta.Icon;
              return (
                <button
                  key={p}
                  onClick={() => setPreviewTargets((arr) => on ? arr.filter((x) => x !== p) : [...arr, p])}
                  data-testid={`ns-publish-${p}`}
                  className={`rounded-md border p-1.5 text-[10px] flex flex-col items-center gap-0.5 transition ${on ? "border-brand-amber bg-brand-amber/10" : "border-line bg-bg-elevated hover:border-brand-amber/40"}`}
                  style={on ? { boxShadow: `0 0 0 1px ${meta.color}66` } : {}}
                >
                  <Icon className="h-3 w-3" style={{ color: meta.color === "#FFFFFF" ? "#F1F5F9" : meta.color }} />
                  <span className="capitalize">{meta.label}</span>
                </button>
              );
            })}
          </div>
          <button
            onClick={onPublish}
            disabled={busy.publish || !bulletin.audio_asset_id || previewTargets.length === 0}
            data-testid="ns-publish-btn"
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-bold text-white disabled:opacity-50"
            style={{ background: "linear-gradient(135deg, #8B5CF6, #FF8A1F)" }}
          >
            {busy.publish ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Publish to {previewTargets.length} target{previewTargets.length === 1 ? "" : "s"}
          </button>
        </section>

        {/* Video Visual Style — preset that flows into News Center's broadcast render */}
        <VisualStyleSelector
          value={visualPick}
          onChange={setVisualPick}
          compact
          data-testid="ns-visual-style"
        />
        <div className="text-[10px] text-ink-muted leading-relaxed -mt-1">
          📺 Your preset travels with you — broadcast this bulletin from{" "}
          <strong className="text-brand-amber">News Center</strong> and it will render with the chosen style.
        </div>
      </div>

      <AiWriteScriptModal
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        defaultScriptType={scriptTypeForBulletin}
        defaultTone={scriptTypeForBulletin === "weather" || scriptTypeForBulletin === "traffic" ? "calm" : "authoritative"}
        defaultLengthSec={targetSeconds}
        initialTopic={bulletin.title || bulletin.raw_script || ""}
        hideTitle
        onApply={({ script }) => {
          setBulletin((b) => ({ ...b, raw_script: script }));
          patch({ raw_script: script });
          setAiOpen(false);
          toast.success("Script inserted. Click AI Format to polish, then Render.");
        }}
      />
    </div>
  );
}

function LibraryView({ tpl, bulletins, loading, folderFilter, setFolderFilter, onOpen, onDelete, onNewType }) {
  const [zipDate, setZipDate] = useState(new Date().toISOString().slice(0, 10));
  const [zipBusy, setZipBusy] = useState(false);

  const dayZip = async () => {
    setZipBusy(true);
    try {
      const r = await api.get(`/news-studio/bulletins-day-zip?date=${zipDate}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bulletins_${zipDate}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success(`Zip for ${zipDate} downloading`);
    } catch (e) {
      let d = e?.response?.data?.detail;
      if (!d && e?.response?.data instanceof Blob) {
        try { d = JSON.parse(await e.response.data.text())?.detail; } catch { /* noop */ }
      }
      toast.error(d || "No rendered bulletins on that day");
    }
    setZipBusy(false);
  };

  return (
    <div className="mt-6 grid lg:grid-cols-4 gap-4" data-testid="ns-library">
      {/* Folder sidebar */}
      <aside className="space-y-1.5">
        <button
          onClick={() => setFolderFilter("")}
          data-testid="ns-folder-all"
          className={`w-full text-left flex items-center gap-2 rounded-lg border px-3 py-2 text-xs transition ${!folderFilter ? "border-brand-amber bg-brand-amber/10 text-brand-amber" : "border-line bg-bg-elevated text-ink-secondary hover:border-brand-amber/40"}`}
        ><Library className="h-3 w-3" /> All bulletins</button>
        {tpl.folders.map((f) => {
          const Icon = ICON_FOR_TYPE[f] || Folder;
          const sel = folderFilter === f;
          return (
            <button
              key={f}
              onClick={() => setFolderFilter(f)}
              data-testid={`ns-folder-${f}`}
              className={`w-full text-left flex items-center gap-2 rounded-lg border px-3 py-2 text-xs transition capitalize ${sel ? "border-brand-amber bg-brand-amber/10 text-brand-amber" : "border-line bg-bg-elevated text-ink-secondary hover:border-brand-amber/40"}`}
            ><Icon className="h-3 w-3" /> {f.replace("_", " ")}</button>
          );
        })}
      </aside>

      {/* Bulletins grid */}
      <div className="lg:col-span-3">
        <div className="flex flex-wrap items-center gap-2 mb-3 rounded-lg border border-line bg-bg-elevated/40 px-3 py-2" data-testid="ns-dayzip-bar">
          <span className="font-mono text-[9px] uppercase tracking-wider text-ink-muted">Automation export</span>
          <input type="date" value={zipDate} onChange={(e) => setZipDate(e.target.value)} data-testid="ns-dayzip-date"
            className="rounded-md border border-line bg-bg-elevated px-2 py-1 text-xs" />
          <button onClick={dayZip} disabled={zipBusy} data-testid="ns-dayzip-btn"
            className="inline-flex items-center gap-1 rounded-md bg-cyan-500/15 border border-cyan-400/50 px-2.5 py-1 text-[10px] font-bold text-cyan-300 hover:bg-cyan-500/25 transition disabled:opacity-50">
            {zipBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />} Download day as ZIP
          </button>
          <span className="text-[10px] text-ink-muted">All of that day&apos;s rendered bulletins in one zip for your automation system.</span>
        </div>
        {loading ? (
          <div className="text-center py-10 text-ink-muted text-xs">
            <Loader2 className="h-4 w-4 mx-auto animate-spin mb-1" /> Loading…
          </div>
        ) : bulletins.length === 0 ? (
          <div className="text-center py-12 rounded-xl border border-dashed border-line text-ink-muted text-sm">
            <Library className="h-5 w-5 mx-auto text-brand-amber mb-2" />
            No bulletins in this folder yet.
            <div className="mt-3"><button onClick={() => onNewType()} className="text-xs text-brand-amber hover:underline">Create one →</button></div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="ns-library-grid">
            {bulletins.map((b) => {
              const Icon = ICON_FOR_TYPE[b.bulletin_type] || Newspaper;
              return (
                <div key={b.bulletin_id} className="rounded-xl border border-line bg-bg-surface p-4 hover:border-brand-amber/40 transition group" data-testid={`ns-card-${b.bulletin_id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <Icon className="h-4 w-4 text-brand-amber" />
                    <span className="font-mono text-[9px] uppercase tracking-wider text-ink-muted">{b.status}</span>
                  </div>
                  <button onClick={() => onOpen(b.bulletin_id)} className="text-left w-full">
                    <div className="font-display font-bold text-sm text-ink-primary line-clamp-2">{b.title}</div>
                    <div className="text-[10px] text-ink-muted mt-1 capitalize">{b.bulletin_type.replace("_", " ")} · {b.duration_key.replace("radio_", "")}s</div>
                  </button>
                  {b.audio_asset_id && (
                    <audio controls preload="none" src={`${API_BASE}/news-studio/bulletins/${b.bulletin_id}/audio`} className="w-full mt-2 h-8" data-testid={`ns-card-audio-${b.bulletin_id}`} />
                  )}
                  <div className="flex items-center justify-between mt-2">
                    <button onClick={() => onOpen(b.bulletin_id)} className="text-[10px] text-brand-amber hover:underline">Open →</button>
                    {b.audio_asset_id && (
                      <a href={`${API_BASE}/news-studio/bulletins/${b.bulletin_id}/audio`}
                        download={`${b.title || "bulletin"}.mp3`}
                        data-testid={`ns-card-download-${b.bulletin_id}`}
                        className="inline-flex items-center gap-1 rounded-md bg-cyan-500/15 border border-cyan-400/50 px-2 py-1 text-[10px] font-bold text-cyan-300 hover:bg-cyan-500/25 transition"
                      ><Download className="h-3 w-3" /> MP3</a>
                    )}
                    <button onClick={() => onDelete(b.bulletin_id)} className="text-ink-muted hover:text-rose-300" data-testid={`ns-card-delete-${b.bulletin_id}`}><Trash2 className="h-3 w-3" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


function AdvancedMixPanel({ mix, setMix, beds }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3 border-t border-line/40 pt-3" data-testid="ns-advanced-mix">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-[10px] uppercase tracking-wider text-cyan-300 hover:text-cyan-100 transition"
        data-testid="ns-advanced-toggle"
      >
        <span className="inline-flex items-center gap-1.5">
          <Sliders className="h-3 w-3" /> Advanced mix
        </span>
        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>
      {open && (
        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          {/* Bed picker */}
          <div className="sm:col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Music bed</Label>
            <Select
              value={mix.bed_subtype}
              onValueChange={(v) => setMix((m) => ({ ...m, bed_subtype: v }))}
            >
              <SelectTrigger className="bg-bg border-line mt-1 text-xs" data-testid="ns-mix-bed"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {beds?.by_category?.length ? (
                  beds.by_category.map((cat) => (
                    <React.Fragment key={cat.category}>
                      <div className="px-2 py-1 text-[9px] font-mono uppercase tracking-wider text-cyan-300/70">
                        {cat.category}
                      </div>
                      {cat.items.map((b) => (
                        <SelectItem key={b.id} value={b.id} className="text-xs">{b.label}</SelectItem>
                      ))}
                    </React.Fragment>
                  ))
                ) : (
                  <SelectItem value="talk_warm" className="text-xs">Talk — Warm conversation</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          <MixSlider label="Voice volume" suffix="dB" min={-12} max={0} step={0.5}
                     value={mix.voice_volume_db}
                     onChange={(v) => setMix((m) => ({ ...m, voice_volume_db: v }))}
                     testid="ns-mix-voice-db" />
          <MixSlider label="Bed volume" suffix="dB" min={-30} max={-6} step={0.5}
                     value={mix.bed_volume_db}
                     onChange={(v) => setMix((m) => ({ ...m, bed_volume_db: v }))}
                     testid="ns-mix-bed-db" />
          <MixSlider label="Fade in" suffix="ms" min={0} max={3000} step={100}
                     value={mix.fade_in_ms}
                     onChange={(v) => setMix((m) => ({ ...m, fade_in_ms: v }))}
                     testid="ns-mix-fade-in" />
          <MixSlider label="Fade out" suffix="ms" min={0} max={4000} step={100}
                     value={mix.fade_out_ms}
                     onChange={(v) => setMix((m) => ({ ...m, fade_out_ms: v }))}
                     testid="ns-mix-fade-out" />

          <label className="sm:col-span-2 flex items-center gap-2 text-xs text-ink-secondary cursor-pointer">
            <input
              type="checkbox"
              checked={!!mix.normalize}
              onChange={(e) => setMix((m) => ({ ...m, normalize: e.target.checked }))}
              data-testid="ns-mix-normalize"
              className="accent-brand-amber"
            />
            Normalize final mix (peak-leveled, broadcast-safe)
          </label>
        </div>
      )}
    </div>
  );
}

function MixSlider({ label, suffix, min, max, step, value, onChange, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-[10px] uppercase tracking-wider text-ink-muted">{label}</Label>
        <span className="font-mono text-[10px] text-brand-amber">{value} {suffix}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testid}
        className="w-full accent-brand-amber"
      />
    </div>
  );
}

function ImportFromNewsCenterModal({ open, onClose, onPick, busy, tpl }) {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [duration, setDuration] = useState("radio_30");
  const [bulletinType, setBulletinType] = useState("headline_news");

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    api.get("/news/stories?limit=100")
      .then((r) => { if (alive) setStories(Array.isArray(r.data) ? r.data : (r.data?.items || [])); })
      .catch(() => toast.error("Couldn't load news stories"))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return stories;
    return stories.filter((s) => {
      const hay = `${s.headline || ""} ${s.summary || ""} ${(s.tags || []).join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [stories, search]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-bg-surface border-line text-ink-primary max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="ns-import-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Inbox className="h-5 w-5 text-cyan-300" /> Import from News Center
          </DialogTitle>
          <DialogDescription className="text-ink-secondary text-sm">
            Pick a news story to seed a new bulletin. The script will auto-populate from the
            story&apos;s {duration === "radio_15" ? "15-second" : duration === "radio_30" ? "30-second" : duration === "radio_60" ? "60-second" : "2-minute"} generated copy (or the headline/summary as fallback).
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 my-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search stories by headline, tags, summary…"
            className="bg-bg border-line text-sm flex-1"
            data-testid="ns-import-search"
          />
          <Select value={bulletinType} onValueChange={setBulletinType}>
            <SelectTrigger className="bg-bg border-line text-xs w-44" data-testid="ns-import-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(tpl?.bulletin_types || []).map((bt) => (
                <SelectItem key={bt.id} value={bt.id} className="text-xs">{bt.icon} {bt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={duration} onValueChange={setDuration}>
            <SelectTrigger className="bg-bg border-line text-xs w-36" data-testid="ns-import-duration"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(tpl?.durations || []).map((d) => (
                <SelectItem key={d.id} value={d.id} className="text-xs">{d.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 overflow-y-auto pr-1 space-y-2 min-h-[200px]">
          {loading ? (
            <div className="text-center py-12 text-ink-muted text-sm">
              <Loader2 className="h-5 w-5 mx-auto animate-spin text-cyan-300 mb-2" />
              Loading stories…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-ink-muted text-sm">
              <FileText className="h-5 w-5 mx-auto text-ink-muted mb-2" />
              No stories match. Ingest a few in the News Center first.
            </div>
          ) : (
            filtered.map((s) => (
              <button
                key={s.story_id}
                onClick={() => onPick(s, duration, bulletinType)}
                disabled={busy}
                data-testid={`ns-import-row-${s.story_id}`}
                className="w-full text-left rounded-lg border border-line bg-bg-elevated px-3 py-2.5 hover:border-cyan-500/60 hover:text-cyan-100 transition disabled:opacity-50"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display font-bold text-sm text-ink-primary line-clamp-1">{s.headline || s.title || "Untitled"}</div>
                    {s.summary && <div className="text-[11px] text-ink-muted line-clamp-2 mt-0.5">{s.summary}</div>}
                  </div>
                  <span className="font-mono text-[9px] uppercase tracking-wider text-ink-muted shrink-0 mt-0.5">{s.status || "ingested"}</span>
                </div>
                {(s.tags || []).length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {(s.tags || []).slice(0, 5).map((t) => (
                      <span key={t} className="text-[9px] font-mono uppercase tracking-wider text-cyan-300/80 border border-cyan-500/30 rounded px-1.5 py-0.5">{t}</span>
                    ))}
                  </div>
                )}
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AutoDraftSettingsModal({ open, onClose, tpl, settings, onSaved }) {
  const [enabled, setEnabled] = useState(false);
  const [interval, setInterval] = useState(60);
  const [duration, setDuration] = useState("radio_30");
  const [dedupe, setDedupe] = useState(true);
  const [autoRender, setAutoRender] = useState(false);
  const [autoSchedule, setAutoSchedule] = useState(false);
  const [autoMyriad, setAutoMyriad] = useState(false);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Hydrate from server when opened
  useEffect(() => {
    if (!open) return;
    if (settings) {
      setEnabled(!!settings.enabled);
      setInterval(Number(settings.interval_minutes || 60));
      setDuration(settings.duration_key || "radio_30");
      setDedupe(settings.dedupe_track !== false);
      setAutoRender(!!settings.auto_render);
      setAutoSchedule(!!settings.auto_schedule_top_of_hour);
      setAutoMyriad(!!settings.auto_push_to_myriad);
    }
    setHistoryLoading(true);
    api.get("/news-studio/auto-history?limit=20")
      .then((r) => setHistory(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  }, [open, settings]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/news-studio/auto-settings", {
        enabled, interval_minutes: interval, duration_key: duration, dedupe_track: dedupe,
        auto_render: autoRender,
        auto_schedule_top_of_hour: autoSchedule && autoRender,
        auto_push_to_myriad: autoMyriad && autoRender,
      });
      onSaved && onSaved(data.settings || {});
      toast.success(enabled ? "Auto-draft enabled" : "Auto-draft disabled");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save settings");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-bg-surface border-line text-ink-primary max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="ns-auto-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Zap className="h-5 w-5 text-emerald-300" /> Auto-draft from Now Playing
          </DialogTitle>
          <DialogDescription className="text-ink-secondary text-sm">
            Keep a fresh music-news bulletin queued automatically. The platform reads what&apos;s
            spinning every few minutes; when your interval elapses AND the track has changed,
            a new draft lands in your <span className="font-mono text-cyan-300">music_news</span> folder ready to render.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 mt-3 space-y-4">
          {/* Enable toggle */}
          <div className="flex items-center justify-between rounded-lg border border-line bg-bg-elevated px-4 py-3">
            <div>
              <div className="text-sm font-semibold text-ink-primary">Enabled</div>
              <div className="text-[11px] text-ink-muted">Master switch for the auto-draft loop on this account.</div>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="ns-auto-enabled" />
          </div>

          {/* Interval */}
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Interval</Label>
            <div className="flex items-center gap-3 mt-1">
              <input
                type="range" min={15} max={240} step={5}
                value={interval}
                onChange={(e) => setInterval(Number(e.target.value))}
                data-testid="ns-auto-interval"
                className="flex-1 accent-emerald-400"
              />
              <span className="font-mono text-xs text-emerald-300 w-20 text-right">{interval} min</span>
            </div>
            <div className="text-[10px] text-ink-muted mt-1">Bulletins draft no more often than this. 15-240 min.</div>
          </div>

          {/* Duration */}
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Bulletin duration</Label>
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger className="bg-bg border-line text-xs mt-1" data-testid="ns-auto-duration"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(tpl?.durations || []).map((d) => (
                  <SelectItem key={d.id} value={d.id} className="text-xs">{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Dedupe */}
          <label className="flex items-center gap-2 text-xs text-ink-secondary cursor-pointer">
            <input
              type="checkbox"
              checked={dedupe}
              onChange={(e) => setDedupe(e.target.checked)}
              data-testid="ns-auto-dedupe"
              className="accent-emerald-400"
            />
            Skip if the same song is still playing (recommended)
          </label>

          {/* Auto-render & top-of-hour schedule — the "hands-free" pipeline */}
          <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-4 py-3 space-y-3" data-testid="ns-auto-render-block">
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-emerald-300/90">
              // Fully hands-free pipeline
            </div>
            <label className="flex items-start gap-2 text-xs text-ink-secondary cursor-pointer">
              <input
                type="checkbox"
                checked={autoRender}
                onChange={(e) => {
                  setAutoRender(e.target.checked);
                  if (!e.target.checked) setAutoSchedule(false);
                }}
                data-testid="ns-auto-render"
                className="accent-emerald-400 mt-0.5"
              />
              <span>
                <span className="text-ink-primary font-semibold">Auto-render the audio</span>
                <div className="text-[10px] text-ink-muted">
                  TTS + ducked-bed mix runs automatically after each draft. Final MP3 lands
                  in the bulletin&apos;s library tile, ready to play. (Uses TTS budget.)
                </div>
              </span>
            </label>
            <label className={`flex items-start gap-2 text-xs cursor-pointer ${autoRender ? "text-ink-secondary" : "text-ink-muted opacity-50"}`}>
              <input
                type="checkbox"
                checked={autoSchedule && autoRender}
                disabled={!autoRender}
                onChange={(e) => setAutoSchedule(e.target.checked)}
                data-testid="ns-auto-schedule-toh"
                className="accent-emerald-400 mt-0.5"
              />
              <span>
                <span className="font-semibold">Schedule for the next top of the hour</span>
                <div className="text-[10px] text-ink-muted">
                  After rendering, mark the bulletin <code className="text-cyan-300">scheduled</code> with
                  the next :00 timestamp — perfect for an automation poller that grabs the latest
                  scheduled MP3 hourly. Requires Auto-render above.
                </div>
              </span>
            </label>

            <label className={`flex items-start gap-2 text-xs cursor-pointer ${autoRender ? "text-ink-secondary" : "text-ink-muted opacity-50"}`}>
              <input
                type="checkbox"
                checked={autoMyriad && autoRender}
                disabled={!autoRender}
                onChange={(e) => setAutoMyriad(e.target.checked)}
                data-testid="ns-auto-myriad"
                className="accent-emerald-400 mt-0.5"
              />
              <span>
                <span className="font-semibold">Push directly into Myriad playout cart wall</span>
                <div className="text-[10px] text-ink-muted">
                  After rendering, push the MP3 straight into your connected Broadcast Radio (Myriad)
                  cart wall via XAPI — automation grabs it on the next pass. End-to-end hands-free
                  pipeline. Requires the Broadcast Radio integration to be configured under Integrations.
                </div>
              </span>
            </label>
          </div>

          {/* Status panel */}
          {settings?.last_run_at && (
            <div className="rounded-lg border border-line/70 bg-bg/50 px-4 py-3 text-[11px] space-y-1">
              <div className="font-mono uppercase tracking-wider text-[9px] text-cyan-300/80">Last run</div>
              <div className="text-ink-secondary">
                <span className="text-ink-primary">{new Date(settings.last_run_at).toLocaleString()}</span>
                {settings.last_status && (
                  <span className={`ml-2 font-mono uppercase text-[9px] ${settings.last_status === "ok" ? "text-emerald-300" : "text-amber-300"}`}>
                    · {settings.last_status}
                  </span>
                )}
              </div>
              {settings.last_track && (
                <div className="text-ink-muted">Last track: <span className="text-cyan-200">{settings.last_track}</span></div>
              )}
            </div>
          )}

          {/* History list */}
          <div>
            <div className="font-mono uppercase tracking-wider text-[9px] text-cyan-300/80 mb-2">Recent auto-drafts</div>
            {historyLoading ? (
              <div className="text-center py-4 text-ink-muted text-xs">
                <Loader2 className="h-4 w-4 mx-auto animate-spin text-cyan-300" />
              </div>
            ) : history.length === 0 ? (
              <div className="text-[11px] text-ink-muted">None yet — toggle on and a draft will appear within {interval} minutes whenever the song changes.</div>
            ) : (
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {history.map((h, i) => (
                  <div key={i} data-testid="ns-auto-history-row"
                       className="rounded-md border border-line/40 bg-bg-elevated px-3 py-1.5 text-[11px] flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <span className={`font-mono uppercase tracking-wider text-[9px] mr-2 ${
                        h.status === "ok" ? "text-emerald-300" : h.status === "skipped" ? "text-amber-300" : "text-rose-300"
                      }`}>{h.status}</span>
                      <span className="text-ink-primary truncate">{h.artist || ""}{h.title ? ` — ${h.title}` : ""}</span>
                    </div>
                    <span className="text-ink-muted shrink-0">{new Date(h.at).toLocaleTimeString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-line">
          <button
            onClick={onClose}
            className="rounded-md border border-line bg-bg-elevated px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary transition"
            data-testid="ns-auto-cancel"
          >Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="ns-auto-save"
            className="inline-flex items-center gap-2 rounded-md bg-emerald-500 px-5 py-2 font-semibold text-black hover:bg-emerald-400 transition disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save settings
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
